<template>

  <DefaultCrudView
    @create-new="newDefaulticon"
    :breadcrumbsItems="breadcrumbs"
    :actions="crudActions"
    containerLarge
  >
    <template slot="content">

      <v-data-table
        :headers="headers"
        :items="defaulticons"
        class="elevation-1"
        :loading="loading"
        :options.sync="options"
        :server-items-length="itemsLength"
        :footer-props="$defaultDataTableFooterProps"
      >
        <template v-slot:item.icon_key="{ item }">
          <v-icon v-if="item.icon_key" class="mr-2">
            {{ getMdiIcon(item.icon_key) }}
          </v-icon>
          {{ item.icon_key }}
        </template>

        <template v-slot:item.actions="{ item }">

          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                class="mr-2"
                @click="editDefaulticon(item)"
              >
                mdi-pencil
              </v-icon>
            </template>
            <span>{{ $t('edit') }}</span>
          </v-tooltip>

          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                class="mr-2"
                @click.stop="deleteDefaulticon(item)"
              >
                mdi-delete
              </v-icon>
            </template>
            <span>{{ $t('delete') }}</span>
          </v-tooltip>

        </template>

      </v-data-table>

      <DefaultModal 
        :title="getModalTitle()" 
        ref="modal" 
        v-on:submit="save"
      >
        <template v-slot:content>
          <DynamicFormContent :fields="fields"></DynamicFormContent>
        </template>
      </DefaultModal>

      <!-- Confirmation Dialog -->
      <v-dialog v-model="confirmDialog" max-width="400">
        <v-card>
          <v-card-title class="headline">{{ $t('confirm') }}</v-card-title>
          <v-card-text>
            {{ $t('confirm_delete') }}
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" text @click="confirmDialog = false">
              {{ $t('close') }}
            </v-btn>
            <v-btn color="red darken-1" text @click="confirmDelete">
              {{ $t('delete') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </template>
  
  </DefaultCrudView>
</template>

<style scoped>
  .container {
    padding: 0;
  }

  .table-container {
    margin-top: 20px;
  }
</style>

<script>

import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import DefaultCrudView from '@/components/DefaultCrudView.vue'
import DefaultModal from '@/components/DefaultModal.vue'
import DynamicFormContent from '@/components/DynamicFormContent.vue'

import DefaulticonActions from '@/store/operation/defaulticon/actions-types'
import DefaulticonMutations from '@/store/operation/defaulticon/mutations-types'
import AppActions from '@/store/app/actions-types'

export default {
  name: "DefaultIcon",

  components: {
    DefaultCrudView,
    DefaultModal,
    DynamicFormContent
  },

  computed: {
    ...mapGetters('app', [
      'getIconForOperationSettingsItem'
    ]),

    ...mapState({
      fields: state => state.operation.defaulticon.fields,
      headers: state => state.operation.defaulticon.tableHeaders,
      selectedDefaulticon: state => state.operation.defaulticon.selectedDefaulticon
    }),

    breadcrumbs() {
      return [
        {
          text: this.$t('default_icons'),
          prependIcon: this.getIconForOperationSettingsItem('default_icons'),
        },
      ]
    }
  },

  data() {
    return {
      loading: true,
      defaulticons: [],
      options: {
        sortBy: ['default_icon_id'],
        sortDesc: [false],
      },
      itemsLength: 0,
      crudActions: [
        {
          text: "create_default_icon",
          eventToEmit: "create-new",
        }
      ],
      confirmDialog: false,
      defaulticonToDelete: null,
    }
  },

  mounted() {
    this[DefaulticonMutations.SET_SELECTED_DEFAULTICON](null)
  },

  watch: {
    options: {
      handler () {
        this.reloadData()
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions('operation/defaulticon', [
      DefaulticonActions.GET_ALL_DEFAULTICONS,
      DefaulticonActions.SAVE_DEFAULTICON,
      DefaulticonActions.UPDATE_DEFAULTICON,
      DefaulticonActions.DELETE_DEFAULTICON,
    ]),

    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE,
      AppActions.OPEN_APP_ERROR_MESSAGE
    ]),

    ...mapMutations('operation/defaulticon', [
      DefaulticonMutations.SET_SELECTED_DEFAULTICON,
    ]),

    newDefaulticon() {
      this[DefaulticonMutations.SET_SELECTED_DEFAULTICON](null)
      this.$refs.modal.openModal()
    },

    save() {
      this.$refs.modal.errorMessage = null
      this.$refs.modal.errorList = []

      this[DefaulticonActions.SAVE_DEFAULTICON]()
        .then(() => {
          let message = this.selectedDefaulticon ? 'Default icon edited' : 'Default icon created'
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t(message))
          this.$refs.modal.closeModal()
          this.reloadData()
        })
        .catch(() => this.$refs.modal.submitting = false)
    },

    reloadData() {
      let options = {...this.$route.query}

      if (this.options.sortBy.length > 0) {
        options.order = this.options.sortBy[0]
      }

      if (this.options.sortDesc.length > 0 && this.options.sortDesc[0]) {
        options.direction = 'DESC'
      }

      // Handle pagination
      if (this.options.itemsPerPage && this.options.itemsPerPage > 0) {
        options.limit = this.options.itemsPerPage
        options.offset = this.options.itemsPerPage * ((this.options.page || 1) - 1)
      }
      
      this.loading = true
      this[DefaulticonActions.GET_ALL_DEFAULTICONS](options)
        .then((result) => {
          // Handle different possible response structures
          if (result && result.items) {
            this.defaulticons = result.items
            this.itemsLength = result.total || result.count || result.items.length
          } else if (Array.isArray(result)) {
            this.defaulticons = result
            this.itemsLength = result.length
          } else if (result && result.data) {
            if (Array.isArray(result.data)) {
              this.defaulticons = result.data
              this.itemsLength = result.data.length
            } else if (result.data.items) {
              this.defaulticons = result.data.items
              this.itemsLength = result.data.total || result.data.count || result.data.items.length
            }
          } else {
            this.defaulticons = []
            this.itemsLength = 0
          }
          this.loading = false
        })
        .catch((error) => {
          console.error('Error loading default icons:', error)
          this.defaulticons = []
          this.itemsLength = 0
          this.loading = false
        })
    },

    editDefaulticon(defaulticon) {
      this[DefaulticonMutations.SET_SELECTED_DEFAULTICON](defaulticon)
      this.$refs.modal.openModal()
    },

    deleteDefaulticon(defaulticon) {
      this.defaulticonToDelete = defaulticon
      this.confirmDialog = true
    },

    confirmDelete() {
      if (this.defaulticonToDelete) {
        this.$store.dispatch('operation/defaulticon/' + DefaulticonActions.DELETE_DEFAULTICON, this.defaulticonToDelete.default_icon_id)
          .then(() => {
            this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('Default icon deleted'))
            this.reloadData()
            this.confirmDialog = false
            this.defaulticonToDelete = null
          })
          .catch((error) => {
            console.error('Error deleting default icon:', error)
            this[AppActions.OPEN_APP_ERROR_MESSAGE](error?.message || this.$t('Error deleting default icon'))
            this.confirmDialog = false
            this.defaulticonToDelete = null
          })
      }
    },

    getModalTitle() {
      return this.selectedDefaulticon ? this.$t('edit_default_icon') : this.$t('new_default_icon')
    },

    getMdiIcon(iconKey) {
      return iconKey.startsWith('mdi-') ? iconKey : `mdi-${iconKey}`
    }
  }
}

</script>
